
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { sendPricingRequest, EmailTemplateParams } from "@/utils/emailService";

export interface LandingFormData {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  username: string;
  password: string;
}

export interface UseLandingFormProps {
  onAccessGranted: () => void;
}

const ACCESS_SESSION_KEY = "appSessionUnlocked";

export const useLandingForm = ({ onAccessGranted }: UseLandingFormProps) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [formData, setFormData] = useState<LandingFormData>({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    username: "",
    password: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value: string) => {
    setFormData(prev => ({ ...prev, country: value }));
  };

  // Function to fill in test credentials
  const fillTestCredentials = () => {
    setFormData({
      firstName: "Test",
      lastName: "User",
      email: "test@example.com",
      country: "United States",
      username: "testuser",
      password: "testpassword123"
    });
    setConsentChecked(true);
    toast.info("Test credentials applied. Click 'Access Product Catalog' to continue.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔵 FORM SUBMIT EVENT FIRED');
    console.log('📋 Form data:', JSON.stringify(formData, null, 2));
    console.log('✅ Consent checked:', consentChecked);
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.country) {
      console.log('❌ LandingForm: Required fields missing');
      toast.error("Please fill in all required fields");
      return;
    }

    // Check for test/fake data - TEMPORARILY DISABLED FOR TESTING
    // const testPatterns = [
    //   /^test$/i,
    //   /^test\s*user$/i,
    //   /^user$/i,
    //   /^sample$/i,
    //   /^example$/i,
    //   /^demo$/i,
    //   /^fake$/i,
    //   /^asdf/i,
    //   /^qwerty/i
    // ];
    
    // const fullName = `${formData.firstName} ${formData.lastName}`.toLowerCase();
    // const isTestData = testPatterns.some(pattern => 
    //   pattern.test(formData.firstName.toLowerCase()) || 
    //   pattern.test(formData.lastName.toLowerCase()) ||
    //   pattern.test(fullName)
    // );

    // if (isTestData) {
    //   console.log('❌ LandingForm: Test/fake data detected');
    //   toast.error("Please enter your real name to access the product catalog");
    //   return;
    // }

    // Check if consent is given
    if (!consentChecked) {
      console.log('❌ LandingForm: Consent not given');
      toast.error("Please consent to receive communications from Aurora Multimedia Corp");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      console.log('❌ LandingForm: Invalid email format');
      toast.error("Please enter a valid email address");
      return;
    }

    // Check for test emails and disposable email services - TEMPORARILY DISABLED FOR TESTING
    // const blockedEmailPatterns = [
    //   'test@', '@test.', '@example.', 'fake@', '@fake.',
    //   '@temp.', '@tempmail.', '@guerrillamail.', '@mailinator.',
    //   '@10minutemail.', '@throwaway.', '@yopmail.', '@sharklasers.',
    //   '@trashmail.', 'noemail@', 'none@', 'na@'
    // ];
    
    // const emailLower = formData.email.toLowerCase();
    // if (blockedEmailPatterns.some(pattern => emailLower.includes(pattern))) {
    //   console.log('❌ LandingForm: Test/disposable email detected');
    //   toast.error("A valid email address is required to access the product catalog");
    //   return;
    // }

    setIsSubmitting(true);
    console.log('⏳ Setting isSubmitting to true');
    
    try {
      // Save access info to local storage FIRST (before email)
      const dataToStore = {
        ...formData,
        consent: consentChecked,
        timestamp: new Date().toISOString()
      };
      
      console.log('💾 LandingForm: Storing data to localStorage:', dataToStore);
      localStorage.setItem("appAccess", JSON.stringify(dataToStore));
      sessionStorage.setItem(ACCESS_SESSION_KEY, "true");
      
      // Verify the data was stored correctly
      const storedData = localStorage.getItem("appAccess");
      console.log('🔍 LandingForm: Verification - stored data:', storedData);
      
      toast.success("Thank you! Redirecting to the product catalog...");
      
      // Send email in background (don't wait for it)
      console.log('📧 LandingForm: Preparing email data...');
      const templateParams: EmailTemplateParams = {
        first_name: formData.firstName,
        last_Name: formData.lastName,
        email_address: formData.email,
        company_name: "N/A",
        job_title: "N/A",
        mobile_number: "N/A",
        Company_Address_Line_1: "N/A",
        Company_Address_Line_2: "N/A",
        Company_Address_Line_3: "N/A",
        Postal_Code: "N/A",
        interests: "Website Access",
        message: `${formData.firstName} ${formData.lastName} from ${formData.country} has accessed the website`,
        from_name: `${formData.firstName} ${formData.lastName}`,
        to_name: "Sales Team",
        country: formData.country,
        consent: "Yes",
        username: formData.username || "N/A",
        password: formData.password || "N/A"
      };

      // Send email in background without blocking the user
      console.log('📤 LandingForm: Sending email in background...');
      sendPricingRequest(templateParams)
        .then(() => console.log('✅ LandingForm: Background email sent successfully'))
        .catch((error) => console.error('❌ LandingForm: Background email failed:', error));
      
      // Redirect to home page immediately
      setTimeout(() => {
        console.log('🏠 LandingForm: Redirecting to home...');
        onAccessGranted();
      }, 1000);
      
    } catch (error: any) {
      console.error("❌ LandingForm: CAUGHT ERROR:", error);
      console.error("❌ Error details:", JSON.stringify(error, null, 2));
      
      // Show detailed error message to user
      const errorMessage = error.message || "There was a problem submitting your information";
      toast.error(errorMessage + ". Please try again or contact support.");
    } finally {
      console.log('🔄 Setting isSubmitting back to false');
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    consentChecked,
    isSubmitting,
    handleChange,
    handleCountryChange,
    setConsentChecked,
    handleSubmit,
    fillTestCredentials
  };
};
